/**
 * OAuth Callback Handler
 * Completes Spotify OAuth flow by calling backend /auth/callback
 */

import { useEffect, useRef, useState } from 'react';
import { Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../features/auth/AuthProvider';
import { getApiBaseUrl } from '../api/client';

export function CallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [notWhitelisted, setNotWhitelisted] = useState(false);
  const authAttempted = useRef(false);

  useEffect(() => {
    // Prevent double-run in StrictMode
    if (authAttempted.current) {
      return;
    }
    authAttempted.current = true;

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    // Handle OAuth error from Spotify - redirect to dedicated error page
    if (errorParam) {
      const params = new URLSearchParams({ error: errorParam });
      if (errorDescription) {
        params.set('error_description', errorDescription);
      }
      navigate(`/spotify-error?${params.toString()}`, { replace: true });
      return;
    }

    // Validate required parameters
    if (!code || !state) {
      setError('Missing authorization code or state parameter');
      setIsLoading(false);
      return;
    }

    // Call backend callback endpoint
    const completeAuth = async () => {
      try {
        const apiBaseUrl = getApiBaseUrl();
        const response = await fetch(
          `${apiBaseUrl}/auth/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: { message: response.statusText } }));

          // Check if user is not whitelisted (403 Forbidden with NOT_WHITELISTED code)
          // FastAPI wraps error response in 'detail' field
          const detail = errorData.detail || errorData;
          if (response.status === 403 && detail.error?.code === 'NOT_WHITELISTED') {
            // Store user data for invitation-required page
            if (detail.user) {
              sessionStorage.setItem('unauthorized_user', JSON.stringify(detail.user));
            }
            setNotWhitelisted(true);
            return;
          }

          throw new Error(errorData.error?.message || 'Authentication failed');
        }

        const data = await response.json();

        // Store tokens and user in auth state
        login(data);

        setSuccess(true);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Authentication failed';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    completeAuth();
  }, [searchParams, login]);

  // Redirect to invitation required page if not whitelisted
  if (notWhitelisted) {
    const userData = sessionStorage.getItem('unauthorized_user');
    return (
      <Navigate
        to="/invitation-required"
        state={{ user: userData ? JSON.parse(userData) : null }}
        replace
      />
    );
  }

  // Redirect to app on success
  if (success) {
    return <Navigate to="/app" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        <div className="bg-gray-800 rounded-lg p-8 shadow-2xl">
          {error ? (
            <>
              <div className="text-red-400 text-xl mb-4">Authentication Failed</div>
              <p className="text-gray-300 mb-6">{error}</p>
              <a
                href="/login"
                className="inline-block bg-[#1DB954] hover:bg-[#1ed760] text-white font-semibold py-2 px-6 rounded-full transition-colors"
              >
                Try Again
              </a>
            </>
          ) : isLoading ? (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1DB954] mx-auto mb-4"></div>
              <p className="text-gray-300">Completing authentication...</p>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
